import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import isYesterday from 'dayjs/plugin/isYesterday'
import utc from 'dayjs/plugin/utc'

import { MessageData } from '../providers/Messages/types'
import {
  PeerGroupAppointment,
  PeerGroupScheduleDay,
} from '../providers/PeerGroups/types'
import {
  ProviderDetails,
  ProviderUser,
} from '../providers/ProviderDetails/types'
import {
  Appointment,
  DayWithAppointments,
  ScheduleDay,
} from '../providers/Schedule/types'
import { User } from '../providers/User/types'

export const getUserInitials = (name: string) => {
  return name.split(' ').length
    ? name
        .split(' ')
        .map((np) => np[0]?.toUpperCase())
        .slice(0, 2)
        .join(' ')
    : undefined
}

export const truncateFileName = (name: string, desiredLength = 10) => {
  const nameParts = name.split('.')
  const extension = nameParts.pop()
  return `${nameParts.join('').substring(0, desiredLength)}${
    nameParts.join().length > desiredLength ? '...' : '.'
  }${extension}`
}

export const generateRandomPassword = (passwordLength: number) => {
  const numberChars = '0123456789'
  const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowerChars = 'abcdefghijklmnopqrstuvwxyz'
  const allChars = numberChars + upperChars + lowerChars
  let randPasswordArray = Array(passwordLength)
  randPasswordArray[0] = numberChars
  randPasswordArray[1] = upperChars
  randPasswordArray[2] = lowerChars
  randPasswordArray = randPasswordArray.fill(allChars, 3)
  return shuffleArray(
    randPasswordArray.map(function (x) {
      return x[Math.floor(Math.random() * x.length)]
    })
  ).join('')
}

export const shuffleArray = (array: any[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }
  return array
}

const APPOINTMENT_TIME_FORMAT = 'h:mm A'
// const PRETTY_FULL_DATE_FORMAT = 'MMMM D, YYYY'
const PRETTY_FULL_DATE_AND_TIME_FORMAT = 'MM/DD/YYYY h:mm A'
const SHORT_DISPLAY_FORMAT = 'MM/DD/YYYY'
const DAY_OF_WEEK_FORMAT = 'dddd'

dayjs.extend(isYesterday)
dayjs.extend(isToday)
dayjs.extend(isTomorrow)
dayjs.extend(duration)

export const dateFormatters = {
  time: (date: string) => dayjs(date).format(APPOINTMENT_TIME_FORMAT),
  prettyDate: (dateString: string) => {
    const day = dayjs(dateString)
    const baseDate = day.format(SHORT_DISPLAY_FORMAT)
    const prepend = day.isYesterday()
      ? 'Yesterday'
      : day.isToday()
      ? 'Today'
      : day.isTomorrow()
      ? 'Tomorrow'
      : day.format('dddd')
    return `${prepend}, ${baseDate}`
  },
  prettyDateWithTime: (date: string) =>
    dayjs(date).format(PRETTY_FULL_DATE_AND_TIME_FORMAT),
  shortenedCleanDate: (date: string) =>
    dayjs(date).format(SHORT_DISPLAY_FORMAT),
  duration: (startTime: string, endTime: string) => {
    const dayjsduration = dayjs.duration(
      dayjs(endTime)
        .second(0)
        .millisecond(0)
        .diff(dayjs(startTime).second(0).millisecond(0))
    )
    const hours = dayjsduration.hours() ? `${dayjsduration.hours()}h ` : ''
    const minutes = dayjsduration.minutes() ? `${dayjsduration.minutes()}m` : ''
    return `${hours}${minutes}`
  },
  imageLabel: () => dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
  dayOfWeek: (day: Date) => dayjs(day).format(DAY_OF_WEEK_FORMAT),
  meetingTimes: (startTime: Date, endTime: Date) => {
    return `${dayjs(startTime).format(APPOINTMENT_TIME_FORMAT)} - ${dayjs(
      endTime
    ).format(APPOINTMENT_TIME_FORMAT)}`
  },
}

dayjs.extend(isBetween)
export const dayUtilities = {
  isComingUp: (
    startTime: string,
    openBeforeInMinutes = 10,
    openAfterInMinutes = 10
  ): boolean => {
    return dayjs().isBetween(
      dayjs(startTime).subtract(120, 'minutes'),
      dayjs(startTime).add(120, 'minutes')
    )
  },
}

dayjs.extend(utc)
export const syncDateAndTime = (date: Date, time: Date): Date => {
  return dayjs()
    .set('year', dayjs(date).year())
    .set('month', dayjs(date).month())
    .set('date', dayjs(date).date())
    .set('hour', dayjs(time).hour())
    .set('minute', dayjs(time).minute())
    .set('second', dayjs(time).second())
    .toDate()
}

export const syncDateAndTimeLocal = (
  date: any,
  time: any
): Date | undefined => {
  return date && time
    ? new Date(`${date.split('T')[0]}T${time.split('T')[1]}`)
    : new Date()
}

export const getStartAndEndDateFromMonthYear = (
  month: number,
  year: number
): { startDate: string; endDate: string } => {
  const date = dayjs().month(month).year(year)
  return {
    startDate: date.startOf('month').toISOString(),
    endDate: date.endOf('month').toISOString(),
  }
}

export const divideScheduleIntoDays = (
  rawAppointments: Appointment[] | PeerGroupAppointment[],
  beginningState: ScheduleDay[] | PeerGroupScheduleDay[] = []
) =>
  (rawAppointments as any).reduce((r: any, a: any) => {
    const day = new Date(a.startTime).toDateString()

    const foundAt = r.findIndex((sd: ScheduleDay) => sd.day === day)
    const foundDayOrNew: ScheduleDay =
      foundAt > -1 ? r[foundAt] : { day, appointments: [] }
    foundDayOrNew.appointments.push(a)

    if (foundAt === -1) {
      r.push(foundDayOrNew)
    }
    return r
  }, beginningState)

export const divideScheduleIntoDaysWithAppointments = (
  schedule: Date[]
): DayWithAppointments[] => {
  const days = (schedule as any).reduce((r: any, a: any) => {
    const day = new Date(a).getDate()
    const foundAt = r.findIndex((sd: DayWithAppointments) => sd.day === day)
    const foundDayOrNew: ScheduleDay =
      foundAt > -1 ? r[foundAt] : { day, appointments: [] }
    foundDayOrNew.appointments.push(a)

    if (foundAt === -1) {
      r.push(foundDayOrNew)
    }
    return r
  }, [])
  return days.map((sd: ScheduleDay) => ({
    day: sd.day,
  }))
}

export const isLastMessageFromYou = (
  message: MessageData,
  user: ProviderUser
): boolean => {
  return message.fromUser.id === user.providerId
}

type getMonthNameByNumType = (num: number) => string
export const getMonthNameByNum: getMonthNameByNumType = (num: number) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const name = months.find((m: string, i: number) => {
    if (i === num) {
      return m
    }
  })
  if (name) {
    return name
  } else return '' //to fix the chart type error
}

export const generateZoomWorkspaceLink = (joinUrl: string) => {
  const url = new URL(joinUrl)
  const meetingId = url.pathname.split('/j/')[1]
  const pwd = url.searchParams.get('pwd') || ''
  const pwdParam = pwd ? `&pwd=${pwd}` : ''
  const ua = navigator.userAgent || ''

  if (/iPad|iPhone|iPod/i.test(ua) || /android/i.test(ua)) {
    return joinUrl
  }

  return `zoommtg://zoom.us/join?confno=${meetingId}${pwdParam}`
}
